import { useState, type ReactElement } from "react";
import { flushSync } from "react-dom";
import { createRoot } from "react-dom/client";
import "../ui/kit/styles.ts";
import { ConfirmDialog, KitInputProvider, type KitInput } from "../ui/kit/index.ts";
import { say } from "../words.ts";
import { logout, type LogoutOutcome } from "./auth.ts";

/**
 * A logout that did not finish (P1.4-T7, ADR-075 decision 6). Log out asks the gateway to end the session and,
 * on a kept device, to clear the kept cookie, which only the gateway can do; a logout that got no answer, or a
 * refusal such as a limit, left both in place. Rather than show the log in form as if the device were
 * forgotten, the page says so over what it was showing, with the answer's sentence, and offers Retry, or
 * leaving anyway, knowing the device may still be logged in.
 */

/**
 * The dialog's width in logical pixels, which holds the centred title on one line in the panel-title face. Its
 * words are `account.logoutRetry.*` in the language files, approved by the author (ADR-075's note of 2026-09-28).
 */
export const LOGOUT_RETRY_WIDTH = 272;

/**
 * Shows the dialog for a logout that did not finish: `run` retries it, and the promise answers true once a
 * retry has finished it, or false when the player leaves anyway.
 */
export type RetryLogout = (run: () => Promise<LogoutOutcome>, message: string) => Promise<boolean>;

/**
 * The dialog: the body, the last answer's sentence as its error, Retry busy while a retry is out, and Leave
 * anyway.
 *
 * @param props The retry, the first failure's sentence and where the dialog answers.
 * @returns The dialog.
 */
function Retrying({ run, message, done }: { readonly run: () => Promise<LogoutOutcome>; readonly message: string; readonly done: (finished: boolean) => void }): ReactElement {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string>(message);
  /**
   * Sends the logout again, keeping the dialog busy until it answers.
   */
  const retry = (): void => {
    setBusy(true);
    void run().then((outcome) => {
      if (outcome.ok) {
        done(true);
        return;
      }
      setBusy(false);
      setError(outcome.message);
    });
  };
  return (
    <ConfirmDialog title={say("account.logoutRetry.title")} confirmLabel={say("account.logoutRetry.retry")} cancelLabel={say("account.logoutRetry.leave")} width={LOGOUT_RETRY_WIDTH} onConfirm={retry} onCancel={() => done(false)} busy={busy} error={error}>
      <p style={{ margin: 0 }}>{say("account.logoutRetry.body")}</p>
    </ConfirmDialog>
  );
}

/**
 * Mounts the dialog's layer over the page on the page's input router.
 *
 * @param host The layer's element.
 * @param input The page's input router.
 * @returns The function that shows the dialog; a second call while one is open answers false at once.
 */
export function mountLogoutRetry(host: HTMLElement, input: KitInput): RetryLogout {
  const root = createRoot(host);
  let open = false;
  return (run, message) => {
    if (open) {
      return Promise.resolve(false);
    }
    open = true;
    return new Promise<boolean>((resolve) => {
      /**
       * Takes the dialog off the page, so it has given focus back before the page moves on, and answers.
       *
       * @param finished Whether a retry finished the logout.
       */
      const done = (finished: boolean): void => {
        flushSync(() => root.render(null));
        open = false;
        resolve(finished);
      };
      root.render(
        <KitInputProvider input={input}>
          <Retrying run={run} message={message} done={done} />
        </KitInputProvider>,
      );
    });
  };
}

/**
 * Logs a session out and, when that did not finish, shows the dialog until a retry finishes it or the player
 * leaves anyway.
 *
 * @param base The account origin.
 * @param refreshToken The session's refresh token.
 * @param retry The dialog.
 * @returns True when the logout finished, false when the player left without it.
 */
export async function logOutWithRetry(base: string, refreshToken: string, retry: RetryLogout): Promise<boolean> {
  const outcome = await logout(base, refreshToken);
  return outcome.ok ? true : await retry(() => logout(base, refreshToken), outcome.message);
}
